import { useQuery } from "@tanstack/react-query";
import { Swords, Calendar, Clock, Flame } from "lucide-react";
import { api } from "@/lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => {
  if (!d) return "TBA";
  const [y, m, day] = d.split("-");
  if (!m || !day) return d;
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
};

function BattleCard({ battle }) {
  return (
    <div className="card-surface relative overflow-hidden rounded-2xl p-6 transition-all hover:-translate-y-1" data-testid={`pk-battle-${battle.id}`}>
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald/70 via-gold/60 to-emerald/70" />

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald/30 to-obsidian ring-2 ring-emerald/40">
            <span className="font-display text-2xl font-bold text-emerald-light">{battle.player1?.[0] || "P"}</span>
          </div>
          <p className="mt-3 truncate text-base font-bold text-slate-100" data-testid={`pk-p1-${battle.id}`}>{battle.player1}</p>
          {battle.player1_id && <p className="mt-0.5 font-mono text-xs text-emerald-light" data-testid={`pk-p1id-${battle.id}`}>ID: {battle.player1_id}</p>}
        </div>

        <div className="flex shrink-0 flex-col items-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full gold-gradient gold-glow">
            <Swords className="h-6 w-6 text-obsidian" />
          </span>
          <span className="mt-2 font-display text-lg font-extrabold gold-text">VS</span>
        </div>

        <div className="flex-1 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gold/25 to-obsidian ring-2 ring-gold/40">
            <span className="font-display text-2xl font-bold gold-text">{battle.player2?.[0] || "P"}</span>
          </div>
          <p className="mt-3 truncate text-base font-bold text-slate-100" data-testid={`pk-p2-${battle.id}`}>{battle.player2}</p>
          {battle.player2_id && <p className="mt-0.5 font-mono text-xs text-gold" data-testid={`pk-p2id-${battle.id}`}>ID: {battle.player2_id}</p>}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-4 border-t border-gold/10 pt-4 text-sm text-slate-300">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-gold" /> {fmtDate(battle.battle_date)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-gold" /> {battle.battle_time || "TBA"}
        </span>
      </div>
    </div>
  );
}

export default function PkBattles() {
  const { data: battles, isLoading } = useQuery({
    queryKey: ["pk-battles"],
    queryFn: async () => (await api.get("/pk-battles")).data,
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-12">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Live Showdown</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">PK Battles</h1>
        <p className="mt-3 text-slate-400">Upcoming head-to-head battles between Golden Live hosts. Don't miss them!</p>
      </div>

      <div className="mt-10 grid gap-5 sm:grid-cols-2">
        {isLoading && <p className="col-span-full text-center text-slate-500">Loading...</p>}
        {!isLoading && (!battles || battles.length === 0) && (
          <div className="card-surface col-span-full flex flex-col items-center gap-3 rounded-2xl py-16 text-center" data-testid="pk-empty">
            <Flame className="h-12 w-12 text-gold/40" />
            <p className="font-bold text-slate-200">No battles scheduled yet</p>
            <p className="text-sm text-slate-500">New PK battles are announced regularly. Check back soon!</p>
          </div>
        )}
        {(battles || []).map((b) => <BattleCard key={b.id} battle={b} />)}
      </div>
    </div>
  );
}
